import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  BellOff,
  Check,
  ChevronDown,
  FileText,
  MoreHorizontal,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { BandaDePipeline } from "@/components/documentos/banda-pipeline";
import { EmptyState } from "@/components/feedback/empty-state";
import { Cabecera } from "@/components/propuesta/cabecera";
import { Tile } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { tramoCorto } from "@/lib/periodo";
import { cn } from "@/lib/utils";

// ===========================================================================
// El checklist de estados de cuenta del mes.
//
// Reemplaza al tablero por etapas, que contestaba la pregunta equivocada. El
// pipeline —recibido, leyendo, leído— dice qué pasó con lo que SÍ llegó, y lo
// que impide cerrar el mes es lo que NO llegó: «¿ya entregué todos los
// extractos?». Esa pregunta se responde por razón social, porque cada estado
// de cuenta está atado a una, y es así como se piden.
//
// Las empresas con algo pendiente van arriba y abiertas; las que están al día
// llegan plegadas. Con 55 razones sociales, una lista alfabética entera obliga
// a leerla completa para encontrar los seis huecos.
// ===========================================================================

type Cuenta = {
  cuentaId: string;
  banco: string;
  numero: string;
  moneda: "PEN" | "USD";
  archivoId: string | null;
  nombre: string | null;
  movimientos: number;
  entro: number;
  salio: number;
  noConciliables: number;
  saldoFinal: number | null;
  desde: string | null;
  hasta: string | null;
  cuadra: boolean | null;
  hallazgos: string[];
  esperada: boolean;
  entregaDesde: string | null;
  entregaHasta: string | null;
};

/**
 * El mes anterior a uno dado, `AAAA-MM`.
 *
 * Es lo que hace falta para dejar de pedirle extracto a una cuenta desde este
 * mes: el rango se cierra en el mes ANTERIOR, no en el que se está mirando —si
 * se cerrara en este, este mes seguiría esperándose.
 */
function mesAnterior(mes: string): string {
  const [a, m] = mes.split("-").map(Number);
  if (!a || !m) return mes;
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, "0")}`;
}

type Empresa = {
  empresaId: string;
  razonSocial: string;
  ruc: string;
  faltan: number;
  cuentas: Cuenta[];
};

const SIMBOLO = { PEN: "S/", USD: "US$" } as const;

/** Céntimos a texto. `null` es «—», que no es lo mismo que cero. */
const dinero = (c: number | null) =>
  c === null
    ? "—"
    : (c / 100).toLocaleString("es-PE", { minimumFractionDigits: 2 });

/** Qué se está mirando. Los filtros son excluyentes: es un checklist. */
type Filtro = "todo" | "faltan" | "noCuadran";

export function Checklist() {
  const [mes, setMes] = useState<string | undefined>(undefined);
  const [filtro, setFiltro] = useState<Filtro>("todo");
  const [busca, setBusca] = useState("");

  const { data, isPending } = api.cobertura.useQuery(
    ["cobertura", mes ?? "ultimo"],
    { query: { periodo: mes } },
  );
  const d = data?.status === 200 ? data.body : null;
  const n = (v: number) => v.toLocaleString("es-PE");

  const empresas = useMemo(() => {
    if (!d) return [];
    const t = busca.trim().toLowerCase();
    const digitos = busca.replace(/\D/g, "");
    return (d.empresas as Empresa[])
      .map((e) => ({
        ...e,
        cuentas: e.cuentas.filter((c) => {
          if (filtro === "faltan" && c.archivoId) return false;
          if (filtro === "noCuadran" && c.cuadra !== false) return false;
          if (!t) return true;
          // Se busca por empresa, RUC, banco o número —y por los dígitos
          // sueltos, que es como se lee un número de cuenta en un papel.
          return (
            e.razonSocial.toLowerCase().includes(t) ||
            e.ruc.includes(digitos || " ") ||
            c.banco.toLowerCase().includes(t) ||
            c.numero.toLowerCase().includes(t) ||
            (digitos.length >= 3 &&
              c.numero.replace(/\D/g, "").includes(digitos))
          );
        }),
      }))
      .filter((e) => e.cuentas.length > 0);
  }, [d, filtro, busca]);

  const faltanEnTotal = d ? d.esperadas - d.entregadas + d.deMas : 0;

  return (
    <>
      <Cabecera
        migas={["Documentos"]}
        titulo="Estados de cuenta"
        acciones={
          <>
            {d && d.periodos.length > 0 ? (
              <select
                aria-label="Mes"
                className="rounded-md border bg-background px-2.5 py-1.5 text-[12.5px]"
                value={d.periodo}
                onChange={(e) => setMes(e.target.value)}
              >
                {d.periodos.map((x: string) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            ) : null}
            <Button size="sm" asChild>
              <Link to="/carga">
                <Upload className="mr-2 size-4" />
                Cargar el mes
              </Link>
            </Button>
          </>
        }
      />

      {isPending || !d ? (
        <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
          {["a", "b", "c", "d"].map((k) => (
            <Skeleton key={k} className="h-24" />
          ))}
        </div>
      ) : d.esperadas === 0 && d.entregadas === 0 ? (
        <EmptyState
          title="Todavía no se le pide extracto a ninguna cuenta"
          message="A una cuenta se le empieza a pedir extracto el mes en que entrega el primero. Cargá uno y desde el mes siguiente aparecerá en este checklist."
          action={
            <Button asChild>
              <Link to="/carga">
                <Upload className="mr-2 size-4" />
                Cargar el mes
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          {/* En qué anda la lectura, y las dos acciones que hacen falta cuando
              algo se atasca: reintentar y pagar al modelo. */}
          <BandaDePipeline clase="estado_cuenta" />

          <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              titulo="Entregados"
              subtitulo={`de ${n(d.esperadas)} que se esperan`}
              valor={n(d.entregadas)}
              tono={
                d.entregadas >= d.esperadas
                  ? "ok"
                  : d.entregadas === 0
                    ? "bad"
                    : "warn"
              }
            />
            <Tile
              titulo="Sin entregar"
              subtitulo="es lo que impide cerrar el mes"
              valor={n(Math.max(0, faltanEnTotal))}
              tono={faltanEnTotal > 0 ? "warn" : "ok"}
            />
            <Tile
              titulo="Cuadran"
              subtitulo="contra el banco o su cadena de saldos"
              valor={n(d.cuadran)}
              tono={d.cuadran === d.entregadas ? "ok" : "warn"}
            />
            <Tile
              titulo="Con hallazgos"
              subtitulo={
                d.deMas > 0
                  ? `${n(d.deMas)} llegaron de cuentas que no se esperaban`
                  : "avisos que dejó la validación"
              }
              valor={n(d.conHallazgos)}
              tono={d.conHallazgos > 0 ? "warn" : "ok"}
            />
          </div>

          {/* Los filtros del checklist. Excluyentes y no acumulables: lo que se
              pregunta aquí es una cosa a la vez —«¿qué falta?»— y unos chips
              que se combinan invitan a construir consultas en vez de mirar. */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-md border p-0.5">
              {(
                [
                  ["todo", "Todo"],
                  [
                    "faltan",
                    `Solo lo que falta${faltanEnTotal ? ` (${faltanEnTotal})` : ""}`,
                  ],
                  ["noCuadran", "Solo lo que no cuadra"],
                ] as const
              ).map(([v, texto]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setFiltro(v)}
                  className={cn(
                    "rounded px-2.5 py-1 font-medium text-[12px] transition-colors",
                    filtro === v
                      ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {texto}
                </button>
              ))}
            </div>
            <div className="flex min-w-[240px] flex-1 items-center gap-2 rounded-md border px-2.5">
              <Search className="size-3.5 shrink-0 text-muted-foreground" />
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Empresa, RUC, banco o número de cuenta"
                className="w-full bg-transparent py-1.5 text-[12.5px] focus:outline-none"
              />
            </div>
          </div>

          {empresas.length === 0 ? (
            <p className="py-10 text-center text-muted-foreground text-sm">
              {filtro === "faltan"
                ? "No falta ningún extracto de este mes."
                : filtro === "noCuadran"
                  ? "Todos los extractos de este mes cuadran."
                  : "Ninguna cuenta coincide con esa búsqueda."}
            </p>
          ) : (
            <div className="space-y-2.5">
              {empresas.map((e) => (
                <GrupoEmpresa
                  key={e.empresaId}
                  empresa={e}
                  periodo={d.periodo}
                  puedeEliminar={d.puedeEliminar}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

function GrupoEmpresa({
  empresa,
  periodo,
  puedeEliminar,
}: {
  empresa: Empresa;
  periodo: string;
  puedeEliminar: boolean;
}) {
  // Abierta si le falta algo. Una empresa al día no necesita mirarse, y con 55
  // razones sociales abrirlas todas convierte el checklist en un muro.
  const [abierta, setAbierta] = useState(empresa.faltan > 0);

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
      <button
        type="button"
        onClick={() => setAbierta((x) => !x)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/50"
      >
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            !abierta && "-rotate-90",
          )}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-[13px]">
            {empresa.razonSocial}
          </span>
          <span className="block text-[11.5px] text-muted-foreground tabular-nums">
            RUC {empresa.ruc} · {empresa.cuentas.length} cuenta
            {empresa.cuentas.length === 1 ? "" : "s"}
          </span>
        </span>
        {empresa.faltan > 0 ? (
          <span className="shrink-0 rounded border border-amber-600/40 bg-amber-500/10 px-2 py-0.5 font-medium text-[11px] text-amber-700 dark:text-amber-500">
            {empresa.faltan} sin entregar
          </span>
        ) : (
          <span className="flex shrink-0 items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-500">
            <Check className="size-3.5" />
            al día
          </span>
        )}
      </button>

      {abierta ? (
        <div className="overflow-x-auto border-t">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b text-[10.5px] text-muted-foreground uppercase tracking-wider">
                <th className="px-4 py-1.5 text-left font-semibold">Banco</th>
                <th className="px-3 py-1.5 text-left font-semibold">Cuenta</th>
                <th className="px-3 py-1.5 text-left font-semibold">Mon.</th>
                {/* DE QUÉ MES ES EL EXTRACTO ENTREGADO. La tabla decía qué
                      trajo cada cuenta y no de cuándo, y el checklist existe
                      justamente para responder «¿me falta alguno?»: sin el mes,
                      un extracto de diciembre cargado en enero cuenta como
                      entregado y el hueco de enero no se ve. */}
                <th className="px-3 py-1.5 text-left font-semibold">Mes</th>
                <th className="px-3 py-1.5 text-right font-semibold">Movs.</th>
                {/* Las apartadas. Al lado del total de líneas y no al final,
                    porque se leen juntas: «80 movimientos, 12 fuera» son 68
                    por decidir, y ese resto es el trabajo que queda. */}
                <th
                  className="px-3 py-1.5 text-right font-semibold"
                  title="Líneas apartadas de la conciliación a propósito"
                >
                  Fuera
                </th>
                <th className="px-3 py-1.5 text-right font-semibold">Entró</th>
                <th className="px-3 py-1.5 text-right font-semibold">Salió</th>
                <th className="px-3 py-1.5 text-right font-semibold">
                  Saldo final
                </th>
                <th className="px-4 py-1.5 text-left font-semibold">Cuadre</th>
                {/* La columna del menú. Sin título: lo que lleva no es un dato
                    de la cuenta, y un encabezado ahí se lee como si lo fuera. */}
                <th className="w-[44px] px-2 py-1.5" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {empresa.cuentas.map((c) => (
                <FilaCuenta
                  key={c.cuentaId}
                  cuenta={c}
                  periodo={periodo}
                  puedeEliminar={puedeEliminar}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function FilaCuenta({
  cuenta: c,
  periodo,
  puedeEliminar,
}: {
  cuenta: Cuenta;
  periodo: string;
  puedeEliminar: boolean;
}) {
  const navegar = useNavigate();
  const qc = useQueryClient();
  const sinEntregar = c.archivoId === null;
  const fijar = api.fijarEntrega.useMutation();
  const eliminar = api.eliminarArchivo.useMutation();

  // La fila entera lleva a los movimientos del extracto, pero el enlace de
  // verdad vive en el número de cuenta: una `<tr>` no puede ser un `<a>` sin
  // romper la tabla, y anidar otra tabla dentro de una celda desalinea todas
  // las columnas. Así hay un destino navegable con el teclado y a la vez se
  // puede pulsar en cualquier parte de la fila.
  const abrir = () => {
    if (c.archivoId)
      navegar({
        to: "/extracto/$archivoId",
        params: { archivoId: c.archivoId },
      });
  };

  /**
   * Dejar de pedirle extracto a esta cuenta.
   *
   * DOS CASOS, y confundirlos deja el maestro mintiendo:
   *
   *  · La cuenta entregó en meses anteriores y se cerró. El rango se corta en
   *    el mes ANTERIOR al que se mira: lo entregado sigue siendo cierto y este
   *    mes deja de esperarse.
   *  · El mes anterior queda por detrás de `entregaDesde` —la cuenta nunca
   *    llegó a entregar nada, o su único extracto se borró—. Cortar ahí daría
   *    un rango al revés, que el servidor rechaza con razón. Lo honesto es
   *    vaciarlo entero: vuelve a «todavía no se le pide», que es de donde
   *    salió, y la primera carga lo rellenará sola.
   */
  const cortarLaEspera = () => {
    const hasta = mesAnterior(periodo);
    const vaciar = !c.entregaDesde || hasta < c.entregaDesde;
    const dice = vaciar
      ? `A ${c.banco} ${c.numero} se le deja de pedir extracto. Vuelve a pedirse sola en cuanto entregue una.`
      : `A ${c.banco} ${c.numero} se le pide extracto hasta ${hasta}, y desde ${periodo} ya no.`;
    if (!window.confirm(dice)) return;

    fijar.mutate(
      {
        params: { cuentaId: c.cuentaId },
        body: vaciar
          ? { desde: null, hasta: null }
          : { desde: c.entregaDesde, hasta },
      },
      {
        onSuccess: (r: { status: number; body: { message?: string } }) => {
          if (r.status !== 200) {
            toast.error(r.body.message ?? "No se pudo cambiar");
            return;
          }
          toast.success(
            vaciar
              ? "Esta cuenta deja de pedirse"
              : `Se le pide extracto hasta ${hasta}`,
          );
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo cambiar"),
      },
    );
  };

  /**
   * Borrar el extracto entregado, con sus movimientos.
   *
   * Se pide el motivo por la misma razón que en la ficha: esto no corrige una
   * carga, la deshace, y dentro de tres meses el renglón de la bitácora es lo
   * único que va a explicar por qué ese mes se cargó dos veces.
   */
  const borrar = () => {
    if (!c.archivoId) return;
    const motivo = window.prompt(
      `Borrar «${c.nombre ?? c.numero}» y todos sus movimientos.\n\n` +
        "¿Por qué? Queda en la bitácora.",
    );
    if (!motivo || motivo.trim().length < 3) return;

    eliminar.mutate(
      {
        params: { id: c.archivoId },
        body: { confirmo: true, motivo: motivo.trim() },
      },
      {
        onSuccess: (r: {
          status: number;
          body: { dice?: string; message?: string };
        }) => {
          if (r.status !== 200) {
            toast.error(r.body.message ?? "No se pudo borrar");
            return;
          }
          toast.success(r.body.dice ?? "Borrado");
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo borrar"),
      },
    );
  };

  return (
    <tr
      className={cn(
        "border-b last:border-0",
        sinEntregar ? "bg-amber-500/5" : "cursor-pointer hover:bg-muted/50",
      )}
      onClick={sinEntregar ? undefined : abrir}
    >
      <td className="px-4 py-2 font-medium">{c.banco}</td>
      <td className="px-3 py-2 tabular-nums">
        {sinEntregar ? (
          c.numero
        ) : (
          <Link
            to="/extracto/$archivoId"
            params={{ archivoId: c.archivoId as string }}
            className="hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {c.numero}
          </Link>
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground">{SIMBOLO[c.moneda]}</td>
      <td className="px-3 py-2">
        {sinEntregar || !tramoCorto(c.desde, c.hasta) ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] tabular-nums">
            {tramoCorto(c.desde, c.hasta)}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {sinEntregar ? "—" : c.movimientos.toLocaleString("es-PE")}
      </td>
      <td
        className={cn(
          "px-3 py-2 text-right tabular-nums",
          // El cero va apagado: lo normal es que no haya ninguna apartada, y
          // una columna de ceros a plena tinta se lee como si pasara algo.
          !sinEntregar && c.noConciliables === 0 && "text-muted-foreground",
        )}
      >
        {sinEntregar ? "—" : c.noConciliables.toLocaleString("es-PE")}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {sinEntregar ? "—" : dinero(c.entro)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {sinEntregar ? "—" : dinero(c.salio)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {sinEntregar ? "—" : dinero(c.saldoFinal)}
      </td>
      <td className="px-4 py-2">
        {sinEntregar ? (
          <span className="flex items-center gap-1.5 font-medium text-[11.5px] text-amber-700 dark:text-amber-500">
            <AlertTriangle className="size-3.5 shrink-0" />
            Sin entregar
          </span>
        ) : c.cuadra === false ? (
          <span
            className="block max-w-[280px] truncate font-medium text-[11.5px] text-red-700 dark:text-red-500"
            title={c.hallazgos.join(" · ")}
          >
            {c.hallazgos[0] ?? "No cuadra"}
          </span>
        ) : c.cuadra === true ? (
          <span className="flex items-center gap-1.5 text-[11.5px] text-emerald-700 dark:text-emerald-500">
            <Check className="size-3.5 shrink-0" />
            Cuadra
          </span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">
            sin validar todavía
          </span>
        )}
        {/* Un extracto que llegó de una cuenta que este mes no se pedía no es
            un error: es una cuenta nueva, o una que se dio por cerrada y
            volvió. Se dice, para que el «32 de 38» siga cuadrando. */}
        {!c.esperada && !sinEntregar ? (
          <span className="block text-[11px] text-muted-foreground">
            no se esperaba este mes
          </span>
        ) : null}
      </td>

      {/* EL MENÚ DE LA FILA.
          Existe porque una fila «Sin entregar» no tenía ninguna acción: se veía
          el hueco y no se podía hacer nada desde ahí. Lo único que permitía
          decir «a esta cuenta ya no se le pide» era la pantalla del maestro, o
          sea el sitio opuesto a donde se nota el problema.
          `stopPropagation` en la celda: la fila entera abre la ficha, y sin eso
          pulsar el menú de una fila entregada navega antes de abrirlo. */}
      <td className="px-2 py-2 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Acciones de ${c.banco} ${c.numero}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <MoreHorizontal className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
            <DropdownMenuLabel>
              {c.banco} {c.numero}
            </DropdownMenuLabel>

            {sinEntregar ? (
              <DropdownMenuItem asChild>
                <Link to="/carga">
                  <Upload />
                  Cargar el extracto
                </Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={abrir}>
                <FileText />
                Abrir la ficha
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {/* Dejar de pedirla se ofrece siempre, entregada o no: una cuenta
                que se cierra a mitad de mes entrega su último extracto y a
                partir del siguiente no debe volver a aparecer. */}
            <DropdownMenuItem
              onSelect={cortarLaEspera}
              disabled={fijar.isPending}
            >
              <BellOff />
              Ya no se le pide extracto
            </DropdownMenuItem>

            {!sinEntregar && puedeEliminar ? (
              <DropdownMenuItem
                variant="destructive"
                onSelect={borrar}
                disabled={eliminar.isPending}
              >
                <Trash2 />
                Eliminar y volver a cargar
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}
